import React, { useEffect, useMemo, useState } from 'react'
import { docCookies } from 'utils/docCookies'
import { useSettings } from 'core/hooks/useSettings'
import { useUserProfile } from 'core/hooks/useDataHook'
import useBulkInvite from 'core/hooks/useBulkInvite'
import useBulkMemberUpdate from 'core/hooks/useBulkMemberUpdate'
import useReportBulkUpdate from 'core/hooks/useReportBulkUpdate'
import usePayPeriodAction from 'core/hooks/usePayPeriodAction'
import usePayPeriodDownload from 'core/hooks/usePayPeriodDownload'
import useMemberExport from 'core/hooks/useMemberExport'
import { matchPath, Outlet } from 'react-router-dom'

import {
  AcceptInviteUrl,
  AccountantsUrl,
  AccountUrl,
  ComplianceOverviewUrl,
  ComplianceUrl,
  DashboardUrl,
  DataExportsUrl,
  LoginUrl,
  PasswordResetUrl,
  PremiumUrl,
  ReportDetailUrl,
  ReportsUrl,
  SignUpAboutYouUrl,
  SignUpCreateTeamUrl,
  SignUpInviteUrl,
  SignUpStepOneUrl,
  SignUpStepThreeUrl,
  SignUpStepTwoUrl,
  SltLoginUrl,
  SplashMessageUrl,
  TeamDashboardUrl,
  TeamsFeatureUrl,
  TransactionsUrl,
  TripsUrl
} from 'constants/RouteUrls'
import useUnitScript from 'core/hooks/useUnitScript'
import useDownloadPeriodSummary from 'core/hooks/useDownloadPeriodSummary'
import useDownloadMergeManagers from 'core/hooks/useDownloadMergeManagers'
import usePeriodsFundingRequest from 'core/hooks/usePeriodsFundingRequest'
import useUserLocation from 'core/hooks/useUserLocation'
import useBrazeInit from 'core/hooks/useBrazeInit'
import useImportTeamPlaces from 'core/hooks/useImportTeamPlaces'
import useMergePublic from 'core/hooks/useMergePublic'
import useUserAnalytics from 'core/hooks/useUserAnalytics'
import useNavigation from 'core/hooks/useNavigation'
import AppLoadingIndicator from 'core/components/loaders/AppLoadingIndicator'
import TopNav from 'core/widgets/top-nav/TopNav'
import ApplyMobileView from 'app/ApplyMobileView'
import MobileWarning from 'app/MobileWarning'
import { Toaster } from 'react-hot-toast'
import FeatureFlagController from 'app/feature-flag/FeatureFlagController'
import ExpiredBankNotification from 'core/widgets/notifications/ExpiredBankNotification'
import MergeSuccessModal from 'core/widgets/merge-success-modal/MergeSuccessModal'
import RatingModal from 'core/widgets/rating-modal/RatingModal'
import TripDetailDrawer from 'core/widgets/trip-detail/TripDetailDrawer'
import TransactionDetailDrawer from 'core/widgets/transaction-detail/TransactionDetailDrawer'
import UnitMFA from 'core/widgets/unit-mfa/UnitMFA'
import useBatchInvite from 'core/hooks/useBatchInvite'
import useBatchDeleteInvite from 'core/hooks/useBatchDeleteInvite'
import useMergePreviewEmployees from 'core/hooks/useMergePreviewEmployees'
import FundingModal from 'core/widgets/funding-modal/FundingModal'

const privateRoutes = [
  AccountantsUrl,
  DashboardUrl,
  PremiumUrl,
  AccountUrl,
  TeamsFeatureUrl,
  TransactionsUrl,
  DataExportsUrl,
  TripsUrl,
  ReportsUrl,
  ComplianceOverviewUrl,
  ComplianceUrl,
  '/team',
  TeamDashboardUrl,
  SignUpAboutYouUrl,
  SignUpStepOneUrl,
  SignUpStepTwoUrl,
  SignUpStepThreeUrl,
  SignUpCreateTeamUrl,
  SignUpInviteUrl,
  SplashMessageUrl,
  AcceptInviteUrl,
  ReportDetailUrl
]

const RouteLayout = () => {
  useUserAnalytics()
  useMergePublic()
  useBrazeInit()
  useUnitScript()
  useBulkInvite()
  useBatchInvite()
  useBatchDeleteInvite()
  useBulkMemberUpdate()
  useImportTeamPlaces()
  useReportBulkUpdate()
  usePayPeriodAction()
  usePayPeriodDownload()
  useMemberExport()
  useDownloadPeriodSummary()
  useDownloadMergeManagers({})
  usePeriodsFundingRequest()
  useMergePreviewEmployees()

  const { getUserLocation } = useUserLocation()
  const { data: settings, set } = useSettings()
  const { featureFlags } = settings
  const [dirty, setDirty] = useState(false)

  const { pathname, search, replace } = useNavigation()

  const isPrivateRoute = useMemo(
    () =>
      !!privateRoutes.find((path) => {
        const m = matchPath(path, pathname)
        return path === m?.pattern.path
      }),
    [pathname]
  )

  const slt = useMemo(() => new URLSearchParams(search).get('slt'), [search])

  const isAuthenticated = !!docCookies.getItem('auth_token')

  const { data: user } = useUserProfile({
    opts: {
      enabled: isAuthenticated,
      onSuccess: (user) => {
        if (!dirty) {
          const newFeatureFlags: Record<string, boolean> = {}
          if (user.teams?.[0]?.enable_payments) {
            newFeatureFlags['ENABLE_EVERLANCE_PAYMENTS'] = true
          }
          if (user.teams?.[0]?.no_program_enabled) {
            newFeatureFlags['ENABLE_NEXT_PROGRAM'] = true
          }
          if (Object.keys(newFeatureFlags).length > 0) {
            set('featureFlags', {
              ...featureFlags,
              ...newFeatureFlags
            })
            setDirty(true)
          }
        }
        getUserLocation()
      },
      onError: (error: any) => {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        if (
          error?.response?.status === 401 ||
          error?.response?.status === 422
        ) {
          if (isPrivateRoute) {
            docCookies.removeItem('auth_token')
            replace(LoginUrl) //todo do not remove
          }
        }
      }
    }
  })

  useEffect(() => {
    const hashPath = window.location.hash.slice(1)
    const match = matchPath(PasswordResetUrl, hashPath)
    if (match) {
      replace(match?.pathname)
    }
  }, [replace])

  if (isAuthenticated && !user) {
    return <AppLoadingIndicator />
  }

  if (!isAuthenticated && slt && !pathname.includes('slt')) {
    replace(`${SltLoginUrl}?slt=${slt}`)
  }

  return (
    <main>
      <FeatureFlagController />
      <ExpiredBankNotification />
      <MergeSuccessModal />
      <RatingModal />
      <FundingModal />

      {/*  Trip Detail Drawer */}
      <TripDetailDrawer />

      {/*  Transaction Detail Drawer */}
      <TransactionDetailDrawer />

      {/* Unit Second Factor Authenticator */}
      <UnitMFA />
      <Toaster
        position="top-right"
        gutter={16}
        toastOptions={{
          duration: 3000
        }}
      />
      <TopNav />
      <ApplyMobileView />
      <MobileWarning />
      <Outlet />
    </main>
  )
}
export default RouteLayout
